// BlogDetail.js
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./BlogDetail.css";
import Header2 from "./Header2/Header2";
import PageTitle from "./pagetitle/PageTitle";
import DOMPurify from "dompurify";
import Footer from "./footer/Footer";

const BlogDetail = () => {
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { slug } = useParams();

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError("");
        // Fetch blog using slug instead of id
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog/slug/${slug}`
        );
        setBlog(response.data.data);

        // Fetch related blogs
        const blogsResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog`
        );

        // Filter out the current blog by slug instead of id
        const randomBlogs = blogsResponse.data.data
          .filter((b) => b.slug !== slug)
          .sort(() => 0.5 - Math.random())
          .slice(0, 6);

        setRelatedBlogs(randomBlogs);
      } catch (err) {
        console.error("Error fetching blog:", err);
        setError("Blog post not found or failed to load");
        navigate("/blogs", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlog();
    }
  }, [slug, navigate]); // Updated dependency from id to slug

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!blog) return <div className="error-message">Blog post not found</div>;

  const createMarkup = (htmlContent) => {
    return { __html: DOMPurify.sanitize(htmlContent) };
  };

  return (
    <>
      <Header2 />

      {/* Dynamic PageTitle with blog title */}
      <PageTitle
        pageTitle={blog.title}
        pageTop="Author Name"
        pagesub={blog.authorName}
      />

      <div className="blog-page-container">
        <Link to="/blogs" className="back-button">
          ← Back to All Blogs
        </Link>

        <div className="blog-content-wrapper">
          <article className="blog-detail-container">
            {/* Featured image moved to the top */}
            {blog.image && (
              <figure className="featured-image-container">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="featured-image"
                />
              </figure>
            )}

            {/* Blog header with meta information */}
            <header className="blog-header">
              <div className="blog-meta">
                <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                <span>•</span>
                <span className="view-count">
                  <i className="far fa-eye"></i> {blog.views || 0} views
                </span>
              </div>

              {/* Meta title and description */}
              <h4 className="meta-title">{blog.metaTitle}</h4>
              <h4 className="meta-description">{blog.metaDescription}</h4>
            </header>

            {/* Blog content */}
            <section
              className="blog-content"
              dangerouslySetInnerHTML={createMarkup(blog.content)}
            ></section>

            {/* Keywords displayed as highlighted buttons at bottom */}
            {blog.keywords?.length > 0 && (
              <div className="blog-keywords-container">
                <div className="blog-keywords">
                  {blog.keywords.map((tag) => (
                    <span key={tag} className="keyword-button">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="blog-navigation">
              <button
                onClick={() => navigate(-1)}
                className="navigation-button"
              >
                ← Back
              </button>
            </div>
          </article>

          <aside className="blog-sidebar">
            {relatedBlogs.length > 0 && (
              <div className="sidebar-section related-blogs-section">
                <h3>More Blogs</h3>
                <div className="related-blogs-list">
                  {relatedBlogs.map((relatedBlog) => (
                    <div key={relatedBlog._id} className="related-blog-item">
                      {/* Update links to use slug-based URLs */}
                      <Link to={`/blog/${relatedBlog.slug}`}>
                        {relatedBlog.image && (
                          <img
                            src={relatedBlog.image}
                            alt={relatedBlog.title}
                            className="related-blog-image"
                          />
                        )}
                        <h4>{relatedBlog.title}</h4>
                      </Link>
                      <p className="related-blog-date">
                        {new Date(relatedBlog.createdAt).toLocaleDateString()}
                      </p>
                      <p className="related-blog-views">
                        <i className="far fa-eye"></i> {relatedBlog.views || 0}{" "}
                        views
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogDetail;